import { Typography } from '@material-ui/core';
import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles';
import { CallEnd, Mic, MicOff, Person, Videocam, VideocamOff } from '@material-ui/icons';
import compose from 'lodash/flowRight';
import { OTPublisher, OTSession, OTStreams, OTSubscriber, preloadScript } from 'opentok-react';
import React, { useEffect, useState } from 'react';

import { colors } from '../themes/colors';

const styles = () =>
  createStyles({
    videoContainer: {
      position: 'relative',
      width: '100%',
      height: '100%',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    subscriberContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute' as 'absolute',
      left: '0',
      top: '0',
      width: '100%',
      height: '100%',
      zIndex: 10 as number,
      '& div': {
        height: '100%',
        width: '100%',
      },
    },
    publisherContainer: {
      position: 'absolute' as 'absolute',
      width: '360px',
      height: '240px',
      bottom: '10px',
      left: '10px',
      zIndex: 1000 as number,
      border: '3px solid white',
      borderRadius: '3px',
      '& div': {
        height: '100%',
        width: '100%',
      },
    },
    videoButtonsContainer: {
      bottom: '20px',
      zIndex: 1000 as number,
      position: 'absolute' as 'absolute',
      display: 'flex',
      width: '100%',
      height: '64.8px',
      justifyContent: 'center',
    },
    sideBarButtonsContainer: {
      bottom: '20px',
      right: '20px',
      zIndex: 1000,
      position: 'absolute',
      display: 'flex',
      justifyContent: 'center',
      width: '150px',
      height: '64.8px',
    },
    videoButton: {
      width: '64.8px',
      height: '64.8px',
      maxWidth: '64.8px',
      maxHeight: '64.8px',
      borderRadius: '50%',
      margin: '0 5px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': {
        boxShadow:
          '0 8px 10px 1px rgba(0,0,0,0.14), 0 3px 14px 2px rgba(0,0,0,0.12), 0 5px 5px 3px rgba(0,0,0,0.2)',
      },
    },
    endCallButton: {
      backgroundColor: colors.red2,
    },
    toggleButtonOn: {
      backgroundColor: colors.gray14,
    },
    toggleButtonOff: {
      backgroundColor: colors.white1,
    },
    largeIconBlack: {
      height: 30,
      width: 30,
      color: colors.black1,
    },
    largeIconWhite: {
      height: 30,
      width: 30,
      color: colors.white1,
    },
    showChat: {
      backgroundColor: colors.blue6,
    },
    showProfile: {
      backgroundColor: colors.blue6,
    },
    warningMessages: {
      position: 'absolute',
      top: -110,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.red2,
      padding: 9,
      borderRadius: 10,
      boxShadow: '0px 11px 20px rgba(60, 78, 98, 0.399858)',
    },
    warningText: {
      display: 'flex',
      alignItems: 'center',
      color: colors.white1,
      fontSize: 16,
      fontWeight: 800,
      lineHeight: '20px',
    },
    smallIcon: {
      width: 16,
      height: 16,
      color: colors.white1,
      marginRight: 5,
    },
  });

type Props = WithStyles<typeof styles> & {
  apiKey?: string;
  sessionId?: string;
  sessionToken?: string;
  onError: () => void;
  onDisconnect: () => void;
  toggleShowChat: () => void;
  toggleShowProfile: () => void;
};

interface SessionHelper {
  disconnect: () => void;
  currentState?: string;
}

const VideoSession: React.FC<Props> = ({
  classes,
  apiKey,
  sessionId,
  sessionToken,
  onError,
  onDisconnect,
  toggleShowChat,
  toggleShowProfile,
}) => {
  const [connected, setConnected] = useState(false);
  const [accessAllowed, setAccessAllowed] = useState<boolean>();

  const [memberConnected, setMemberConnected] = useState(false);

  const [providerVideoEnabled, setProviderVideoEnabled] = useState(true);
  const [providerAudioEnabled, setProviderAudioEnabled] = useState(true);
  const [memberVideoEnabled, setMemberVideoEnabled] = useState<boolean>();
  const [memberAudioEnabled, setMemberAudioEnabled] = useState();

  const defaultSessionhelper: SessionHelper = {
    disconnect: () => null,
  };
  const [sessionHelper, setSessionHelper] = useState(defaultSessionhelper);

  useEffect(() => {
    return sessionHelper.disconnect;
  }, [sessionHelper]);

  const sessionEvents = {
    sessionConnected: (connectionResponse: any): void => {
      setSessionHelper(connectionResponse.target || defaultSessionhelper);
      setConnected(true);
    },
    sessionDisconnected: () => {
      setConnected(false);
      onDisconnect();
    },
    streamCreated: (event) => {
      setMemberVideoEnabled(event.stream.hasVideo);
      setMemberAudioEnabled(event.stream.hasAudio);
      setMemberConnected(true);
    },
    streamDestroyed: () => setMemberConnected(false),
    streamPropertyChanged: (event) => {
      if (event.changedProperty === 'hasAudio' && event.stream.publisher === null) {
        setMemberAudioEnabled(event.newValue);
      }
    },
  };

  const publisherEvents = {
    accessAllowed: () => setAccessAllowed(true),
    accessDenied: () => setAccessAllowed(false),
  };

  const subscriberEvents = {
    videoDisabled: () => setMemberVideoEnabled(false),
    videoEnabled: () => setMemberVideoEnabled(true),
  };

  const toggleAudio = () => {
    setProviderAudioEnabled(!providerAudioEnabled);
  };

  const toggleVideo = () => {
    setProviderVideoEnabled(!providerVideoEnabled);
  };

  return (
    <div className={classes.videoContainer}>
      <OTSession
        apiKey={apiKey}
        sessionId={sessionId}
        token={sessionToken}
        eventHandlers={sessionEvents}
        onError={onError}
        className={classes.videoContainer}
      >
        {connected ? (
          <React.Fragment>
            <div className={classes.publisherContainer}>
              <OTPublisher
                properties={{
                  showControls: false,
                  width: '100%',
                  height: '100%',
                  buttonDisplayMode: 'on',
                  fitMode: 'cover',
                  insertMode: 'append',
                  publishAudio: providerAudioEnabled,
                  publishVideo: providerVideoEnabled,
                }}
                eventHandlers={publisherEvents}
                className={classes.publisherContainer}
              />
            </div>

            <div className={classes.videoButtonsContainer}>
              {accessAllowed === false ||
              !providerAudioEnabled ||
              !providerVideoEnabled ||
              !memberConnected ||
              !memberVideoEnabled ||
              !memberAudioEnabled ? (
                <div className={classes.warningMessages}>
                  {accessAllowed === false ? (
                    <Typography className={classes.warningText}>
                      Access to your camera and/or microphone has been denied
                    </Typography>
                  ) : null}

                  {!providerAudioEnabled && !providerVideoEnabled ? (
                    <Typography className={classes.warningText}>
                      <VideocamOff className={classes.smallIcon} />
                      <MicOff className={classes.smallIcon} />
                      Your camera and mic are turned off
                    </Typography>
                  ) : !providerAudioEnabled ? (
                    <Typography className={classes.warningText}>
                      <MicOff className={classes.smallIcon} />
                      Your mic is turned off
                    </Typography>
                  ) : !providerVideoEnabled ? (
                    <Typography className={classes.warningText}>
                      <VideocamOff className={classes.smallIcon} />
                      Your camera is turned off
                    </Typography>
                  ) : null}

                  {!memberConnected ? (
                    <Typography className={classes.warningText}>
                      Waiting for member to join the session...
                    </Typography>
                  ) : !memberVideoEnabled && !memberAudioEnabled ? (
                    <Typography className={classes.warningText}>
                      <VideocamOff className={classes.smallIcon} />
                      <MicOff className={classes.smallIcon} />
                      Your client's camera and mic are turned off
                    </Typography>
                  ) : !memberVideoEnabled ? (
                    <Typography className={classes.warningText}>
                      <VideocamOff className={classes.smallIcon} />
                      Your client's camera is turned off
                    </Typography>
                  ) : !memberAudioEnabled ? (
                    <Typography className={classes.warningText}>
                      <MicOff className={classes.smallIcon} />
                      Your client's mic is turned off
                    </Typography>
                  ) : null}
                </div>
              ) : null}

              {providerAudioEnabled ? (
                <div
                  className={`${classes.videoButton} ${classes.toggleButtonOn}`}
                  onClick={toggleAudio}
                >
                  <Mic className={classes.largeIconWhite} />
                </div>
              ) : (
                <div
                  className={`${classes.videoButton} ${classes.toggleButtonOff}`}
                  onClick={toggleAudio}
                >
                  <MicOff className={classes.largeIconBlack} />
                </div>
              )}

              <div
                className={`${classes.videoButton} ${classes.endCallButton}`}
                onClick={sessionHelper.disconnect}
              >
                <CallEnd className={classes.largeIconWhite} />
              </div>

              {providerVideoEnabled ? (
                <div
                  className={`${classes.videoButton} ${classes.toggleButtonOn}`}
                  onClick={toggleVideo}
                >
                  <Videocam className={classes.largeIconWhite} />
                </div>
              ) : (
                <div
                  className={`${classes.videoButton} ${classes.toggleButtonOff}`}
                  onClick={toggleVideo}
                >
                  <VideocamOff className={classes.largeIconBlack} />
                </div>
              )}
            </div>

            <div className={classes.sideBarButtonsContainer}>
              <div
                className={`${classes.videoButton} ${classes.showProfile}`}
                onClick={toggleShowProfile}
              >
                <Person className={classes.largeIconWhite} />
              </div>
            </div>

            <OTStreams>
              <div className={classes.subscriberContainer}>
                <OTSubscriber
                  properties={{
                    showControls: false,
                    width: '100%',
                    height: '100%',
                    buttonDisplayMode: 'on',
                    fitMode: 'contain',
                    insertMode: 'append',
                  }}
                  eventHandlers={subscriberEvents}
                />
              </div>
            </OTStreams>
          </React.Fragment>
        ) : (
          <h1>Connecting...</h1>
        )}
      </OTSession>
    </div>
  );
};

export default compose([withStyles(styles), preloadScript])(VideoSession);
